import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { PostHogService } from '../services/posthog.service';

/**
 * Blocks routes gated behind a PostHog feature flag. Attach with the flag key
 * in route data:
 *
 *   {
 *     path: 'connections',
 *     loadChildren: ...,
 *     canActivate: [FeatureFlagGuard],
 *     data: { featureFlag: 'connectable-accounts' }
 *   }
 *
 * Waits for flags to load (bounded — see PostHogService.waitForFeatureFlag)
 * so a deep link hit right after a cold start isn't rejected just because the
 * flags request is still in flight. Flag off or unresolvable → redirect to
 * /bookings, whose AuthGuard bounces logged-out users on to /login.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagGuard implements CanActivate {
  constructor(private postHogService: PostHogService, private router: Router) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const flag = route.data?.featureFlag;
    if (!flag) {
      // Misconfigured route (guard attached without a flag key) — don't
      // strand the user; let them through and flag it for the developer.
      console.error('[FeatureFlagGuard] route is missing data.featureFlag', route.routeConfig?.path);
      return true;
    }
    const enabled = await this.postHogService.waitForFeatureFlag(flag);
    return enabled ? true : this.router.parseUrl('/bookings');
  }
}
